import {
  emptyCategory,
  emptyPricing,
  emptyUnit,
} from '@/components/forms/types/emptyForm';
import {
  WarehousesRow,
  toInventoryWarehouseAssociationInput,
  toNonInventoryWarehouseAssociationInput,
} from '@/components/supplyItems/types/WarehouseRows';
import {
  CatalogStatus,
  InventoryCheckBehavior,
  InventoryItem,
  InventoryItemInput,
  ItemTag,
  ManufacturedType,
  Money,
  NonInventoryItem,
  NonInventoryItemInput,
  StandingOrderBehavior,
  SupplyItem,
  Warehouse,
} from '@/graphql/types/generated';
import { WarehouseAssociation } from '@/types';
import { priceInput, priceOutput } from '@/utils/PricingIO';
import {
  ProcurementRow,
  toProcurementInput,
} from '../../supplyItems/types/ProcurementRow';
import { toMaxQuantityInput } from './maxQuantityPerLocation';

export const defaultDimensionalFactor = 194;

export const createBaseItem = (): SupplyItem & {
  warehouseAssociation: WarehouseAssociation[];
} => ({
  brandName: '',
  brand: undefined,
  catalogStatus: CatalogStatus.NotInCatalog,
  category: emptyCategory,
  createdAt: new Date(),
  createdBy: '',
  description: undefined,
  id: '',
  imgUrl: '',
  itemSize: '',
  size: {
    quantity: 1,
    unit: emptyUnit,
    label: '',
  },
  name: '',
  purchasePrice: emptyPricing,
  saleUnit: emptyUnit,
  standingOrderBehavior: StandingOrderBehavior.NotAvailable,
  subcategory: emptyCategory,
  updatedAt: new Date(),
  updatedBy: '',
  vendorCode: '',
  warehouseAssociation: [],
  dimensionalFactor: defaultDimensionalFactor,
  eachHeightInches: undefined,
  eachLengthInches: undefined,
  eachWeightLbs: undefined,
  eachWidthInches: undefined,
  caseHeightInches: undefined,
  caseLengthInches: undefined,
  caseWeightLbs: undefined,
  caseWidthInches: undefined,
  dimensioned: false,
  casesPerLayer: undefined,
  layersPerPallet: undefined,
  nmfc: undefined,
  refrigerationState: undefined,
  tags: [],
  hasOwnImage: false,
  isShippingRestricted: false,
  promoted: false,
  pinned: false,
  substitutionOverrides: [],
  substitutionPreview: [],
  maxQuantityPerLocation: null,
  sampleMaxQuantityPerLocation: null,
});

type AbstractItemUpsertForm = NonInventoryItem | InventoryItem;

type UpsertItemFormFields = {
  uiProcurementRows: ProcurementRow[];
  uiWarehouseRows: WarehousesRow[];
  isInventoryType: boolean;
};

export type UpsertNonInventoryItemForm = NonInventoryItem &
  UpsertItemFormFields;

export type UpsertInventoryItemForm = InventoryItem & UpsertItemFormFields;

export type UpsertItemForm =
  | UpsertNonInventoryItemForm
  | UpsertInventoryItemForm;

/**
 * Creates an empty form for creation of new {@link SupplyItem}
 * @param isInventoryItem true to create an {@link InventoryItem}, false for {@link NonInventoryItem}
 */
const createEmptyForm = (isInventoryItem: boolean): AbstractItemUpsertForm => {
  const baseItem: SupplyItem = createBaseItem();
  if (isInventoryItem) {
    return {
      ...baseItem,
      __typename: 'InventoryItem',
      inventoryCheckBehavior: InventoryCheckBehavior.Check,
      locationShelfLifeDays: null,
      marketPrice: false,
      perishability: undefined,
      barcodes: [],
      sampleUpcCodes: [],
      shelfLifeDays: 24,
      stockUnit: emptyUnit,
      purchaseUnit: emptyUnit,
      procurementRecords: [],
      upcCodes: [],
      excludedFromPickingLabels: false,
      isSerialized: false,
    };
  }
  return {
    ...baseItem,
    __typename: 'NonInventoryItem',
    minItemsPerOrder: 1,
    purchaseMultiple: 1,
    marketPrice: false,
    markupPercentage: null,
    excludedFromPickingLabels: false,
  };
};

/**
 * Creates a form with the item we are editing {@link SupplyItem}
 * @param item the {@link UpsertItemForm}
 */
export const createEditForm = (item: UpsertItemForm): UpsertItemForm => {
  const pricing: Money = priceOutput(item?.purchasePrice);
  return {
    ...item,
    purchasePrice: pricing,
  };
};

/**
 * Returns the imgUrl to send to the backend.
 * When category image fallback is enabled, only send imgUrl if item has its own image.
 */
const getImgUrlForInput = (
  item: { imgUrl?: string | null; hasOwnImage?: boolean },
  isCategoryImageFallbackEnabled: boolean,
): string | null | undefined => {
  if (isCategoryImageFallbackEnabled && !item.hasOwnImage) {
    return null;
  }
  return item.imgUrl;
};

/**
 * The item's and its sample's lifetime limits per location, as the item mutations take them.
 * IMS refuses a sample limit without a sample unit, so without one the sample limit is sent as null.
 */
const toMaxQuantityInputs = (item: {
  maxQuantityPerLocation?: number | null;
  sampleMaxQuantityPerLocation?: number | null;
  sampleUnit?: { id: string } | null;
}) => ({
  maxQuantityPerLocation: toMaxQuantityInput(item.maxQuantityPerLocation),
  sampleMaxQuantityPerLocation: item.sampleUnit?.id
    ? toMaxQuantityInput(item.sampleMaxQuantityPerLocation)
    : null,
});

type ToNonInventoryItemInputParams = {
  item: NonInventoryItem;
  uiWarehouseRows: WarehousesRow[];
  disablePriceEditing: boolean;
  selfServiceFulfillmentWarehouse: boolean;
  isCategoryImageFallbackEnabled?: boolean;
};

/**
 * Converts a {@link NonInventoryItem} to a {@link NonInventoryItemInput}
 */
export const toNonInventoryItemInput = ({
  item,
  uiWarehouseRows,
  disablePriceEditing,
  isCategoryImageFallbackEnabled = false,
}: ToNonInventoryItemInputParams): NonInventoryItemInput => {
  const associationInputs = uiWarehouseRows
    .filter((v) => v.enabled || v.vendorId)
    .map((v) =>
      toNonInventoryWarehouseAssociationInput(v, disablePriceEditing),
    );

  return {
    brandName: item.brand?.name ?? item.brandName,
    brand: item.brand?.id,
    catalogStatus: item.catalogStatus,
    category: item.category.id,
    description: item.description || null,
    imgUrl: getImgUrlForInput(item, isCategoryImageFallbackEnabled),
    itemSize: item.itemSize,
    size:
      item.size !== undefined
        ? {
            quantity: Number(item.size!.quantity),
            unit: item.size!.unit.id!,
          }
        : undefined,
    marketPrice: item.marketPrice,
    markupPercentage:
      item.marketPrice &&
      item.markupPercentage !== null &&
      item.markupPercentage !== undefined
        ? Number(item.markupPercentage)
        : null,
    minItemsPerOrder: Number(item.minItemsPerOrder),
    ...toMaxQuantityInputs(item),
    name: item.name,
    purchaseMultiple: Number(item.purchaseMultiple),
    purchasePrice: priceInput(item.purchasePrice),
    saleUnit: item.saleUnit.id,
    sampleUnit: item.sampleUnit?.id,
    standingOrderBehavior: item.standingOrderBehavior,
    subcategory: item.subcategory?.id,
    vendorCode: item.vendorCode,
    warehouseAssociation: associationInputs,
    dimensionalFactor:
      item.dimensionalFactor !== null ? Number(item.dimensionalFactor) : null,
    eachHeightInches:
      item.eachHeightInches !== null ? Number(item.eachHeightInches) : null,
    eachLengthInches:
      item.eachLengthInches !== null ? Number(item.eachLengthInches) : null,
    eachWeightLbs:
      item.eachWeightLbs !== null ? Number(item.eachWeightLbs) : null,
    eachWidthInches:
      item.eachWidthInches !== null ? Number(item.eachWidthInches) : null,
    caseHeightInches:
      item.caseHeightInches !== null ? Number(item.caseHeightInches) : null,
    caseLengthInches:
      item.caseLengthInches !== null ? Number(item.caseLengthInches) : null,
    caseWeightLbs:
      item.caseWeightLbs !== null ? Number(item.caseWeightLbs) : null,
    caseWidthInches:
      item.caseWidthInches !== null ? Number(item.caseWidthInches) : null,
    refrigerationState: item.refrigerationState,
    transitMethod: item.transitMethod,
    tags: item.tags ? item.tags.map(({ id }: ItemTag) => id) : null,
    nmfc: item.nmfc,
    casesPerLayer:
      item.casesPerLayer !== null ? Number(item.casesPerLayer) : null,
    layersPerPallet:
      item.layersPerPallet !== null ? Number(item.layersPerPallet) : null,
    dimensioned: item.dimensioned,
  };
};

type ToInventoryItemInputParams = {
  item: InventoryItem;
  uiWarehouseRows: WarehousesRow[];
  disablePriceEditing: boolean;
  selfServiceFulfillmentWarehouse: boolean;
  // TODO: make `uiProcurementRows` and `warehouses` required arguments after
  // updating the bulk upload logic to support entering procurement records.
  uiProcurementRows?: ProcurementRow[];
  warehouses?: Warehouse[];
  isCategoryImageFallbackEnabled?: boolean;
};

/**
 * Converts a {@link  NonInventoryItem} to a {@link NonInventoryItemInput}
 */
export const toInventoryItemInput = ({
  item,
  uiWarehouseRows,
  disablePriceEditing,
  selfServiceFulfillmentWarehouse,
  uiProcurementRows,
  warehouses,
  isCategoryImageFallbackEnabled = false,
}: ToInventoryItemInputParams): InventoryItemInput => {
  const isCustomBrandedItem: boolean =
    item.manufacturedType === ManufacturedType.CustomBrandedItem;
  const associationInputs = uiWarehouseRows
    .filter(
      (v) =>
        v.enabled ||
        v.vendorId ||
        (selfServiceFulfillmentWarehouse &&
          v.configuredFulfillmentWarehouses?.length > 0) ||
        (isCustomBrandedItem && v.salePrice !== undefined),
    )
    .map((v) => toInventoryWarehouseAssociationInput(v, disablePriceEditing));

  const procurementInput =
    uiProcurementRows && warehouses
      ? uiProcurementRows.flatMap((pr) => toProcurementInput(pr, warehouses))
      : null;

  return {
    brandName: item.brand?.name ?? item.brandName,
    brand: item.brand?.id,
    catalogStatus: item.catalogStatus,
    category: item.category.id,
    description: item.description || null,
    imgUrl: getImgUrlForInput(item, isCategoryImageFallbackEnabled),
    itemSize: item.itemSize,
    isSerialized: item.isSerialized,
    size:
      item.size !== undefined
        ? {
            quantity: Number(item.size!.quantity),
            unit: item.size!.unit.id!,
          }
        : undefined,
    inventoryCheckBehavior: item.inventoryCheckBehavior,
    locationShelfLifeDays:
      item.locationShelfLifeDays !== null
        ? Number(item.locationShelfLifeDays)
        : null,
    marketPrice: item.marketPrice,
    ...toMaxQuantityInputs(item),
    name: item.name,
    perishability: item.perishability,
    purchasePrice: priceInput(item.purchasePrice),
    purchaseUnit: item.purchaseUnit.id,
    refrigerationState: item.refrigerationState,
    saleUnit: item.saleUnit.id,
    sampleUnit: item.sampleUnit?.id,
    sampleUpcCodes: item.sampleUpcCodes,
    shelfLifeDays:
      item.shelfLifeDays !== null ? Number(item.shelfLifeDays) : null,
    standingOrderBehavior: item.standingOrderBehavior,
    stockUnit: item.stockUnit?.id,
    subcategory: item.subcategory?.id,
    upcCodes: item.upcCodes,
    vendorCode: item.vendorCode,
    warehouseAssociation: associationInputs,
    procurementRecords: procurementInput,
    dimensionalFactor:
      item.dimensionalFactor !== null ? Number(item.dimensionalFactor) : null,
    eachHeightInches:
      item.eachHeightInches !== null ? Number(item.eachHeightInches) : null,
    eachLengthInches:
      item.eachLengthInches !== null ? Number(item.eachLengthInches) : null,
    eachWeightLbs:
      item.eachWeightLbs !== null ? Number(item.eachWeightLbs) : null,
    eachWidthInches:
      item.eachWidthInches !== null ? Number(item.eachWidthInches) : null,
    caseHeightInches:
      item.caseHeightInches !== null ? Number(item.caseHeightInches) : null,
    caseLengthInches:
      item.caseLengthInches !== null ? Number(item.caseLengthInches) : null,
    caseWeightLbs:
      item.caseWeightLbs !== null ? Number(item.caseWeightLbs) : null,
    caseWidthInches:
      item.caseWidthInches !== null ? Number(item.caseWidthInches) : null,
    transitMethod: item.transitMethod,
    tags: item.tags ? item.tags.map(({ id }: ItemTag) => id) : null,
    nmfc: item.nmfc,
    casesPerLayer:
      item.casesPerLayer !== null ? Number(item.casesPerLayer) : null,
    layersPerPallet:
      item.layersPerPallet !== null ? Number(item.layersPerPallet) : null,
    dimensioned: item.dimensioned,
  };
};

/**
 * Dummy text until product/design
 */
export const loremIpsumDolorAcet = 'Lorem ipsum dolor acet';
export const nameToolTip = 'The name of the item.';
export const brandToolTip = 'The name of the brand that produces the item.';
export const categoryToolTip =
  'The section the item should be listed in for easy navigation.';
export const subcategoryToolTip =
  'The subsection the item should be listed in (ie. Milk - Dairy)';
export const descriptionToolTip =
  "The product description as listed on the vendor's website or catalog.";
export const imageUrlToolTip =
  'A photo of the item to be displayed in the Catalog. Images should be in JPG or PNG format and a maximum of 5MB.';
export const vendorOrderingCodeToolTip =
  'How the procurement vendor denotes the item.';
export const minItemsPerOrderToolTip =
  'The minimum number of items required to purchase.';
export const purchasePriceToolTip =
  'The cost of each individual item from the vendor.';
export const upcCodeToolTip = 'The unique ID of the item.';
export const saleUnitToolTip = 'The number of units required to be purchased.';
export const sampleUnitToolTip = 'The number of units provided for sampling.';
export const sampleUpcCodeToolTip = 'The unique ID of the sample.';
export const stockUnitToolTip =
  'The number of units used to stock this item in inventory.';
export const perishableToolTip = 'The estimated shelf life of the item.';
export const catalogStatusToolTip =
  'Toggling this enables customers to add this item to their Catalog, provided the given warehouse has the item.';
export const vendorCodeToolTip = "Vendor's Ordering Code.";
export const itemSizeToolTip =
  'Measure of a unit of the item, e.g. 12 oz, 1 gallon. Can be in weight, volume, or if none of those, then just "each"';
export const purchaseUnitToolTip =
  'How many units are purchased at a time for storage.';
export const locationShelfLifeDaysToolTip =
  'The shelf life of the item when we receive it into our warehouse';
export const shelfLifeDaysToolTip =
  'The number of days that should be remaining until an item expires when we deliver it.';
export const perishabilityToolTip = 'The estimated shelf life of the item.';
export const samplesDescription =
  'Some products are able to be sent as samples to our network. Please indicate the sample unit below.';
export const sampleSalesUnitToolTip = 'Sample Unit';
export const catalogDescriptionToolTip =
  'Additional information needed to publish an item in the catalog.';
export const enableinCatalogToolTip =
  'Toggling this enables customers to add this item to their Catalog, provided the given warehouse has the item.';
export const standingOrderToolTip =
  'Whether or not the item will be available for standing orders.';
export const skipOOSOrders =
  'Permission to skip Out-Of-Stock checks for this item prior to ordering, with the expectation it will always be in stock.';
export const refrigerationStateToolTip =
  'Whether or not the item requires refrigeration during storage and transport.';
export const minQuantityToOrderToolTip =
  'The minimum number of items required to purchase.';
export const maxQuantityPerLocationToolTip =
  "The most one location can ever order of this item, across all orders. Cancelled orders don't count, and admins can go over it. Leave empty for no limit.";
export const sampleMaxQuantityPerLocationToolTip =
  "The most one location can ever order of this item's sample, across all orders. Cancelled orders don't count, and admins can go over it. Leave empty for no limit.";
export const purchaseMultipleToolTip =
  'The number of items recieved per purchase order.';
export const ordersDescriptionToolTip =
  'The way in which orders for this item should be classified.';
export const inventoryItemDescription =
  'Inventory items are currently carried in our warehouse(s).';
export const locationItemsDescription = `Add this item to a location's catalog by selecting which locations to add below.`;
export const procurementInfoDescription =
  'Information necessary to publish the item in our inventory.';
export const NonInventoryItemDescription =
  'Non-Inventory Item - An item supplied by other vendors.';
export const transitMethodToolTip =
  'Represents how inventory arrives at an Odeko facility, e.g., pick-up, drop-off. This impacts landed cost of owned inventory';
export const FormId = 'ItemForm';
export const modalHeader = 'Unsaved Changes';
export const modalContent =
  'You have unsaved changes. Are you sure you want to leave without saving them?';
export const modalRedirectButtonText = 'Leave and clear';

export const modalCloseButtonText = 'Stay and review';

export const eachLengthToolTip =
  'Length of a singular inventory or non-inventory item in inches';
export const eachWidthToolTip =
  'Width of a singular inventory or non-inventory item in inches';
export const eachHeightToolTip =
  'Height of a singular inventory or non-inventory item in inches';
export const eachWeightToolTip =
  'Weight of a singular inventory or non-inventory item in inches';
export const caseLengthToolTip =
  'Length of a singular inventory or non-inventory case in inches';
export const caseWidthToolTip =
  'Width of a singular inventory or non-inventory case in inches';
export const caseHeightToolTip =
  'Height of a singular inventory or non-inventory case in inches';
export const caseWeightToolTip =
  'Weight of a singular inventory or non-inventory case in pounds';
export const eachVolumeToolTip =
  'L x W x H of a singular inventory or non-inventory item in cubic inches';
export const caseVolumeToolTip =
  'L x W x H of a case of inventory or non-inventory items in cubic inches';

export const dimensionalFactorToolTip =
  'Represents the volume of a package allowed per unit of weight as assigned by FedEx. Currently, this factor is contractually set at 194.';

export const eachDimensionalWeightToolTip =
  'The amount of space an item occupies in relation to its actual weight';
export const caseDimensionalWeightToolTip =
  'The amount of space a case of items occupies in relation to its actual weight';

export const itemTagsDescriptionToolTip =
  'Tags are used to better classify items and appear better on search';

export const isSerializedToolTip =
  'Mark the item as a "Serialized Item" in NetSuite (i.e., an item that has a serial number). This cannot be modified after the item is published.';

export const marketPriceToolTip =
  'Enable market pricing for this item. When enabled, the price will be set at fulfillment time rather than using the purchase price.';

export const markupPercentageToolTip =
  'The markup percentage applied to vendor cost to compute the selling price for market-priced items.';

export const dimensionedToolTip =
  'Indicate whether the item has been processed through the dimensioning tool';

export const casesPerLayerToolTip =
  'How many cases of said item fit in a layer on a pallet';
export const casesPerPalletToolTip =
  'How many cases of said item fit on a pallet (layersPerPallet * casesPerLayer)';
export const layersPerPalletToolTip =
  'How many layers of said item fit on a pallet';

export const palletWeightLbsToolTip =
  'The total weight of a pallet if it were filled completely with cases of said item; includes 45lbs for for the pallet';
export const nmfcToolTip =
  'The 5-digit National Motor Freight Classification (NMFC) code used to classify freight for shipping. This code determines shipping rates and handling requirements.';

export default createEmptyForm;
